import { DOCUMENT, NgClass, NgOptimizedImage, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  HostListener,
  Inject,
  inject,
  Input,
  model,
  OnInit,
  Signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'src/app/core/interfaces';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { LocaleDataPipe } from 'src/app/shared/pipes/locale-data.pipe';
import { EventTypeEnumerate } from '../../../../core/enumerates';
import { AnalyticsStoreService, GenericEventService } from '../../../../shared/services/';
import { AppStoreNavDropdownLinksComponent } from '../store-nav-dropdowns/store-nav-dropdown-links/store-nav-dropdown-links.component';
import { AppStoreNavDropdownComponent } from '../store-nav-dropdowns/store-nav-dropdown/store-nav-dropdown.component';
import { PrefetchService } from '@scalefast/ecommerce-core';
import { trigger, transition, style, animate, state, keyframes } from '@angular/animations';

@Component({
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    NgOptimizedImage,
    RouterModule,
    AppStoreNavDropdownComponent,
    AppStoreNavDropdownLinksComponent,
    LocaleDataPipe,
  ],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [style({ opacity: 0 }), animate('500ms ease-in', style({ opacity: 1 }))]),
      transition(':leave', [animate('500ms ease-out', style({ opacity: 0 }))]),
    ]),
    trigger('openClose', [
      state(
        'open',
        style({
          gridTemplateRows: '1fr',
          visibility: 'visible',
        }),
      ),
      state(
        'closed',
        style({
          visibility: 'hidden',
          gridTemplateRows: '0fr',
        }),
      ),
      transition('open => closed', [
        animate(
          '500ms ease-out',
          keyframes([
            style({ gridTemplateRows: '1fr', visibility: 'visible', offset: 0 }), // Start fully visible
            style({ gridTemplateRows: '0fr', visibility: 'visible', offset: 0.99 }), // Animate but keep visibility
            style({ visibility: 'hidden', offset: 1 }), // Hide at the last moment
          ]),
        ),
      ]),
      transition('closed => open', [
        style({ visibility: 'hidden', gridTemplateRows: '0fr' }), // Start hidden
        animate(
          '500ms ease-in',
          keyframes([
            style({ visibility: 'visible', gridTemplateRows: '0fr', offset: 0 }), // Set visibility before animation starts
            style({ visibility: 'visible', gridTemplateRows: '1fr', offset: 1 }), // Animate to full size
          ]),
        ),
      ]),
    ]),
  ],
  selector: 'app-store-nav-item',
  templateUrl: 'store-nav-item.component.html',
  styleUrls: ['./store-nav-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppStoreNavItemComponent implements OnInit {
  @Input() item!: MenuItem;
  @Input() index!: number;

  opened = model(-1);
  isOpen = computed(() => this.opened() === this.index);
  isMobile: boolean = false;
  animationTrigger: boolean = true;
  shapeColor: string | null = null;
  fontColor: string | null = null;
  linksMenu: boolean = false;
  isActive: Signal<boolean | undefined>;
  #prefetchService = inject(PrefetchService);

  closeDropdown() {
    this.opened.set(-1);
  }

  openDropdown() {
    this.opened.set(this.index);
  }

  toggleDropdown() {
    this.opened.update((i) => (i === this.index ? -1 : this.index));
  }

  constructor(
    private genericEventService: GenericEventService,
    private analyticsStoreService: AnalyticsStoreService,
    private router: Router,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isActive = toSignal(this.isActive$);
  }

  @HostListener('window:resize', [])
  onResize() {
    const docWidth = this.document.documentElement.offsetWidth;
    const wasMobile = this.isMobile;
    this.isMobile = docWidth < 992;

    // Close dropdowns when device changes from mobile to desktop view
    if (wasMobile !== this.isMobile) {
      this.opened.set(-1);
      this.animationTrigger = false; // Temporarily disable animations
      this.cdr.detectChanges(); // Prevents change detection from triggering animations
      this.animationTrigger = true; // Reactivate animations
    }
  }

  private get isActive$() {
    return this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((event) => {
        return event instanceof NavigationEnd;
      }),
      map((event) => {
        //force NavigationEnd, angular compiler is too stupid to infer it.
        return (event as NavigationEnd).url === this.item.linkedPage;
      }),
    );
  }

  ngOnInit(): void {
    this.linksMenu = this.item.dropdownStyle === 'links' ? true : false;
    this.genericEventService.subscribe(EventTypeEnumerate.APP_MENU_CLICK, (event: any) => {
      if (!event.status) {
        this.closeDropdown();
      }
    });
    this.changeItemColors();
  }

  clickItem(): void {
    this.genericEventService.broadcast({ name: EventTypeEnumerate.APP_MENU_CLICK, status: this.isOpen() });
  }

  changeItemColors() {
    if (this.item.customizeLink.shapeColor) {
      this.shapeColor = this.item.customizeLink.shapeColor;
    }
    if (this.item.customizeLink.fontColor) {
      this.fontColor = this.item.customizeLink.fontColor;
    }
  }

  gtmEvent(event: any): void {
    const category = event.currentTarget.getAttribute('data-gtmcategory');
    const action = event.currentTarget.getAttribute('data-gtmaction');
    const label = event.currentTarget.innerText;
    this.analyticsStoreService.miscEvent(category, action, label);
  }

  prefetchPage(item: MenuItem) {
    if (item.linkedPage) {
      const [path, queryParams = ''] = item.linkedPage.split(/\?(.*)/);
      const queryParamsObject = Object.fromEntries(new URLSearchParams(queryParams));
      this.#prefetchService.prefetchPage(path, queryParamsObject);
    }
    if (item.childLinks) {
      item.childLinks.forEach((child) => {
        this.prefetchPage(child);
      });
    }
  }
}
